import { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Delete,
  Edit,
  Error,
  ExpandLess,
  ExpandMore,
  MoreVert,
} from '@material-ui/icons';
import { observer } from 'mobx-react';
import { ConfirmDialog, DropdownMenu, FileUpload, FileUploadArea, UploadedDocument } from '@roc/ui';
import { Document } from '@roc/feature-types';
import { formatCurrency } from '@roc/ui/utils';
import { useLienPayoffReviewStore } from '../../hooks/useLienPayoffReviewStore';
import { AddEditLienModal } from './addEditLienModal';

export const PAYOFF_LETTER_LIEN_SOURCE = 'Payoff Letter';

export interface PropertyLien {
  propertyLienId: number;
  loanId?: number;
  propertyId?: number;
  lienType: string;
  madeTo: string;
  madeBy: string;
  originalAmount: number;
  requiresPayoff?: boolean;
  recordedDate: string;
  datedDate: string;
  // Whether the evidence/extraction address matched this lien's resolved property. Null =
  // not checked / inconclusive; only an explicit false drives the address-mismatch banner.
  addressMatched?: boolean | null;
  // Stored "Payoff N" number on the property; kept when other payoffs are deleted.
  payoffSequence?: number | null;
  sourceType?: string;
  createdBy?: string;
  createdDate?: string;
  lastUpdatedBy?: string;
  lastUpdatedDate?: string;
}

export interface LienPayoffRecord {
  lienPayoffRecordId: number;
  propertyLienId: number;
  lenderName?: string;
  maturityDate?: string;
  borrowerName?: string;
  payoffDate?: string;
  payoffAmount?: number;
  // The amount as extracted from the letter; differs from payoffAmount once an underwriter edits it.
  extractedPayoffAmount?: number | null;
  // JSON object extracted by the AI from the payoff letter, in the collateral Refi Amount format:
  // fixed fee fields (PAYOFF_FEE_FIELDS) plus others[{name, amount}].
  payoffBreakdown?: string | null;
  comment?: string;
  propertyAddress?: string;
  addressMatched?: boolean | null;
  // Letter extraction: IN_PROGRESS on upload, then PROVIDED or FAILED.
  status?: string | null;
  filePath?: string;
  originalFileName?: string;
  displayName?: string;
  createdBy?: string;
  createdDate?: string;
  lastUpdatedBy?: string;
  lastUpdatedDate?: string;
}

export interface PayoffBreakdownItem {
  description: string;
  amount: number;
  // Optional note under the row, e.g. the names of the fees grouped into Other Fees.
  detail?: string;
}

export const PAYOFF_FEE_FIELDS = [
  { key: 'unpaidPrincipalBalance', label: 'Unpaid Principal Balance' },
  { key: 'unpaidRegularInterest', label: 'Unpaid Regular Interest' },
  { key: 'lateFees', label: 'Late Fees' },
  { key: 'defaultInterest', label: 'Default Interest' },
  { key: 'extensionFees', label: 'Extension Fees' },
] as const;

export type PayoffFeeField = typeof PAYOFF_FEE_FIELDS[number]['key'];

// Payoff fee breakdown in the collateral Refi Amount format: fixed fee fields plus others.
export type PayoffBreakdown = Partial<Record<PayoffFeeField, number>> & {
  others?: { name: string; amount: number }[];
};

const toAmount = (value: unknown): number | null =>
  value != null && value !== '' && !isNaN(Number(value)) ? Number(value) : null;

export const parsePayoffBreakdownObject = (raw?: string | null): PayoffBreakdown | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    const breakdown: PayoffBreakdown = {};
    PAYOFF_FEE_FIELDS.forEach(({ key }) => {
      const amount = toAmount(parsed[key]);
      if (amount != null) breakdown[key] = amount;
    });
    breakdown.others = (Array.isArray(parsed.others) ? parsed.others : [])
      .filter(item => item?.name && toAmount(item?.amount) != null)
      .map(item => ({ name: String(item.name), amount: toAmount(item.amount) }));
    return breakdown;
  } catch {
    return null;
  }
};

export const sumPayoffBreakdown = (breakdown: PayoffBreakdown | null): number =>
  breakdown
    ? PAYOFF_FEE_FIELDS.reduce((sum, { key }) => sum + (breakdown[key] ?? 0), 0) +
      (breakdown.others ?? []).reduce((sum, item) => sum + item.amount, 0)
    : 0;

// Flattens the fixed fee fields (only those the letter shows) and the others list into rows.
export const parsePayoffBreakdown = (raw?: string | null): PayoffBreakdownItem[] => {
  const breakdown = parsePayoffBreakdownObject(raw);
  if (!breakdown) return [];
  const fixed = PAYOFF_FEE_FIELDS.filter(({ key }) => breakdown[key] != null).map(
    ({ key, label }) => ({ description: label, amount: breakdown[key] })
  );
  // Every other fee on the letter is shown as one Other Fees row; the names become its description.
  const others = breakdown.others ?? [];
  const otherFees = others.length
    ? [{
        description: 'Other Fees',
        amount: others.reduce((sum, item) => sum + item.amount, 0),
        detail: others.map(item => item.name).join(', '),
      }]
    : [];
  return [...fixed, ...otherFees];
};

export interface PropertyLienPayoff {
  lien: PropertyLien;
  payoffRecord: LienPayoffRecord | null;
}

export interface LoanPropertyOption {
  propertyId: number;
  fullAddress: string;
}

// Display-only: a payoff has evidence once a payoff letter is uploaded for it.
export const hasEvidence = (record: PropertyLienPayoff) => !!record.payoffRecord?.filePath;

// The uploaded letter's extraction status, in StatusDiv terms. Letters uploaded before the status was
// tracked have none and are already extracted.
export const getLetterStatus = (payoffRecord: LienPayoffRecord | null): string | null => {
  if (!payoffRecord?.filePath) return null;
  switch (payoffRecord.status) {
    case 'IN_PROGRESS':
    case 'FAILED':
      return payoffRecord.status;
    case 'MORE_INFO_NEEDED':
      return 'FLAGGED';
    default:
      return 'SUCCESS';
  }
};

// The letter's address is checked against the lien's property. A matching letter's amount replaces the
// payoff amount (no amount mismatch flag); a mismatching one is flagged and its amount isn't used.
export const hasAddressMismatch = (record: PropertyLienPayoff) =>
  record.payoffRecord?.addressMatched === false || record.lien.addressMatched === false;

const useStyles = makeStyles(theme => ({
  card: {
    border: '1px solid #E0E0E0',
    borderRadius: 8,
    marginBottom: theme.spacing(3),
    overflow: 'hidden',
    boxShadow: 'none',
    '&:before': {
      display: 'none',
    },
    '&.Mui-expanded': {
      margin: 0,
      marginBottom: theme.spacing(3),
    },
  },
  cardHeader: {
    backgroundColor: '#FAFAFA',
    padding: theme.spacing(2),
    borderBottom: 'none',
    '&.Mui-expanded': {
      borderBottom: '1px solid #E0E0E0',
      minHeight: 0,
    },
  },
  cardHeaderContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    margin: 0,
    '&.Mui-expanded': {
      margin: 0,
    },
  },
  cardDetails: {
    display: 'block',
    padding: 0,
  },
  cardHeaderActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  cardTitle: {
    fontWeight: 700,
    fontSize: '1rem',
  },
  cardSubtitle: {
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
    marginTop: theme.spacing(0.5),
  },
  statusChip: {
    fontWeight: 600,
    borderRadius: 16,
  },
  chipPending: {
    backgroundColor: '#FFF4E5',
    color: '#B26A00',
  },
  chipProvided: {
    backgroundColor: '#E8F1FC',
    color: '#1565C0',
  },
  cardBody: {
  },
  payoffDataPanel: {
    backgroundColor: '#FAFAFA',
    padding: theme.spacing(2.5),
  },
  evidenceColumn: {
    padding: theme.spacing(2.5),
  },
  columnLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.9rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    marginBottom: theme.spacing(1.5),
  },
  dataRow: {
    display: 'flex',
    padding: theme.spacing(1.5, 0),
    borderBottom: '1px solid #EEEEEE',
  },
  dataRowLast: {
    borderBottom: 'none',
  },
  dataLabel: {
    width: 100,
    flexShrink: 0,
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
  },
  dataValue: {
    fontSize: '0.85rem',
    fontWeight: 700,
    textAlign: 'left',
  },
  extractedData: {
    marginTop: theme.spacing(2),
  },
  extractedRow: {
    marginBottom: theme.spacing(2),
  },
  extractedTwoCol: {
    display: 'flex',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  extractedCol: {
    flex: 1,
  },
  extractedLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  extractedValue: {
    fontSize: '0.85rem',
    fontWeight: 700,
  },
  warningIcon: {
    fontSize: '1rem',
    color: theme.palette.error.main,
  },
  expandIcon: {
    fontSize: '1rem',
    color: theme.palette.text.secondary,
  },
  uploadingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    border: '1px solid #E0E0E0',
    borderRadius: 8,
    backgroundColor: '#FAFAFA',
  },
  payoffAmountToggle: {
    cursor: 'pointer',
    userSelect: 'none',
  },
  breakdown: {
    marginTop: theme.spacing(1),
    border: '1px solid #EEEEEE',
    borderRadius: 6,
    padding: theme.spacing(1, 1.5),
  },
  breakdownRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(0.5, 0),
    fontSize: '0.8rem',
  },
  breakdownDetail: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    fontStyle: 'italic',
    marginTop: theme.spacing(-0.5),
    paddingBottom: theme.spacing(0.5),
  },
  breakdownTotalRow: {
    borderTop: '1px solid #EEEEEE',
    marginTop: theme.spacing(0.5),
    paddingTop: theme.spacing(1),
    fontWeight: 700,
  },
  breakdownMismatch: {
    color: theme.palette.error.main,
    fontSize: '0.75rem',
    marginTop: theme.spacing(0.5),
  },
  noEvidence: {
    padding: theme.spacing(2),
    border: '1px dashed #E0E0E0',
    borderRadius: 8,
    color: theme.palette.text.secondary,
  },
}));

// Full fee breakdown behind the extracted payoff amount - expanded by default (collapsible on
// demand), with the line items' sum checked against the total so a mismatch is visible.
const PayoffAmountBreakdown = ({ payoffRecord }: { payoffRecord: LienPayoffRecord }) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(true);
  const items = useMemo(
    () => parsePayoffBreakdown(payoffRecord.payoffBreakdown),
    [payoffRecord.payoffBreakdown]
  );
  const itemsTotal = items.reduce((sum, item) => sum + item.amount, 0);
  // The breakdown is the letter's, so it's checked against the letter's amount, not an edited one.
  const letterAmount = payoffRecord.extractedPayoffAmount ?? payoffRecord.payoffAmount;
  const reconciles = letterAmount == null || Math.abs(itemsTotal - letterAmount) < 0.01;

  return (
    <Box className={classes.extractedRow}>
      <Typography
        className={`${classes.extractedLabel} ${items.length ? classes.payoffAmountToggle : ''}`}
        onClick={() => items.length && setExpanded(!expanded)}
      >
        Full Payoff Amount
        {items.length > 0 &&
          (expanded ? (
            <ExpandLess className={classes.expandIcon} />
          ) : (
            <ExpandMore className={classes.expandIcon} />
          ))}
      </Typography>
      <Typography className={classes.extractedValue}>
        {payoffRecord.payoffAmount != null ? formatCurrency(payoffRecord.payoffAmount) : ''}
      </Typography>
      {items.length > 0 && (
        <Collapse in={expanded}>
          <Box className={classes.breakdown}>
            {items.map((item, i) => (
              <Box key={`${item.description}-${i}`}>
                <Box className={classes.breakdownRow}>
                  <span>{item.description}</span>
                  <span>{formatCurrency(item.amount)}</span>
                </Box>
                {item.detail && <Typography className={classes.breakdownDetail}>{item.detail}</Typography>}
              </Box>
            ))}
            <Box className={`${classes.breakdownRow} ${classes.breakdownTotalRow}`}>
              <span>Total</span>
              <span>{formatCurrency(itemsTotal)}</span>
            </Box>
          </Box>
          {!reconciles && (
            <Typography className={classes.breakdownMismatch}>
              {`The fee breakdown (${formatCurrency(itemsTotal)}) doesn't add up to the payoff amount (${formatCurrency(letterAmount)}). Please review the payoff letter.`}
            </Typography>
          )}
        </Collapse>
      )}
    </Box>
  );
};

export const LienPayoffReviewCard = observer(
  ({
    record,
    ordinal,
    readOnly = false,
    internalView = false,
  }: {
    record: PropertyLienPayoff;
    ordinal: number;
    readOnly?: boolean;
    // Internal portal (underwriting): extracted data and Add/Edit Payoff. TPO (external portal):
    // upload only - the extracted data stays hidden.
    internalView?: boolean;
  }) => {
    const classes = useStyles();
    const { lienPayoffReviewStore } = useLienPayoffReviewStore();
    const { lien, payoffRecord } = record;
    const [uploading, setUploading] = useState(false);
    const [editLienModalOpen, setEditLienModalOpen] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const evidenceProvided = hasEvidence(record);
    const addressMismatch = hasAddressMismatch(record);
    // Always open by default; the user can collapse and reopen it.
    const [expanded, setExpanded] = useState(true);

    const handleDelete = async () => {
      setDeleteConfirmOpen(false);
      try {
        await lienPayoffReviewStore.deletePayoff(lien.propertyLienId);
      } catch {
        // error notification already shown by the store
      }
    };

    const menuOptions = [
      {
        icon: Edit,
        name: 'Edit Payoff',
        action: () => setEditLienModalOpen(true),
      },
      {
        icon: Delete,
        name: 'Delete',
        action: () => setDeleteConfirmOpen(true),
      },
    ];

    const handleUpload = async (file: File) => {
      setUploading(true);
      try {
        await lienPayoffReviewStore.uploadLienPayoffFile(
          file,
          lien.propertyLienId,
          payoffRecord?.lienPayoffRecordId
        );
      } catch {
        // error notification already shown by the store
      } finally {
        setUploading(false);
      }
    };

    const handleView = () => {
      if (payoffRecord?.lienPayoffRecordId) {
        lienPayoffReviewStore.previewLienPayoffFile(
          payoffRecord.lienPayoffRecordId,
          payoffRecord.originalFileName
        );
      }
    };

    return (
      <>
      <Accordion className={classes.card} expanded={expanded} onChange={(_, isExpanded) => setExpanded(isExpanded)}>
        <AccordionSummary
          className={classes.cardHeader}
          classes={{ content: classes.cardHeaderContent }}
          expandIcon={<ExpandMore />}
          aria-controls={`lien-payoff-card-${lien.propertyLienId}-content`}
          id={`lien-payoff-card-${lien.propertyLienId}-header`}
        >
          <Box>
            <Typography className={classes.cardTitle}>
              {`Payoff ${ordinal}`}
            </Typography>
            <Typography className={classes.cardSubtitle}>
              {[
                lien.lienType,
                lien.originalAmount != null ? formatCurrency(lien.originalAmount) : 'Amount pending',
                lien.madeBy && `Borrower: ${lien.madeBy}`,
                lien.madeTo && `Lender: ${lien.madeTo}`,
                lien.recordedDate && `Recorded ${lien.recordedDate}`,
              ]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
          </Box>
          <Box
            className={classes.cardHeaderActions}
            onClick={event => event.stopPropagation()}
            onFocus={event => event.stopPropagation()}
          >
            {internalView && addressMismatch && <Error className={classes.warningIcon} />}
            <Chip
              size="small"
              label={evidenceProvided ? 'Evidence Provided' : 'Evidence Required'}
              className={`${classes.statusChip} ${evidenceProvided ? classes.chipProvided : classes.chipPending}`}
            />
            {/* Lien management is internal only - the TPO just uploads evidence. */}
            {internalView && !readOnly && (
              <DropdownMenu options={menuOptions}>
                <MoreVert fontSize="small" />
              </DropdownMenu>
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails className={classes.cardDetails}>
        <Grid container className={classes.cardBody}>
          {internalView && (
          <Grid item xs={5} className={classes.payoffDataPanel}>
            <Typography className={classes.columnLabel}>
              Payoff Data
            </Typography>
            {(() => {
              const rows = [
                ['Lien type', lien.lienType],
                ['Amount', lien.originalAmount != null ? formatCurrency(lien.originalAmount) : ''],
                ['Borrower', lien.madeBy],
                ['Lender', lien.madeTo],
                ['Dated', lien.datedDate],
                ['Recorded', lien.recordedDate],
              ];
              return rows.map(([label, value], i) => (
                <Box
                  key={label}
                  className={`${classes.dataRow} ${i === rows.length - 1 ? classes.dataRowLast : ''}`}
                >
                  <Typography className={classes.dataLabel}>{label}</Typography>
                  <Typography className={classes.dataValue}>{value}</Typography>
                </Box>
              ));
            })()}
          </Grid>
          )}
          <Grid item xs={internalView ? 7 : 12} className={classes.evidenceColumn}>
            <Typography className={classes.columnLabel}>
              Evidence
            </Typography>
            {/* Same upload as the trailing docs review (DocReviewDialogV2): the uploaded letter with View and
                Re-upload, or the drop box when there's none yet. The read-only collateral view only shows the file. */}
            {payoffRecord?.filePath ? (
              <UploadedDocument
                document={{ originalFileName: payoffRecord.originalFileName ?? payoffRecord.displayName } as Document}
                onView={handleView}
                onUpload={readOnly ? undefined : handleUpload}
                disabled={uploading || getLetterStatus(payoffRecord) === 'IN_PROGRESS'}
              />
            ) : readOnly ? (
              <Typography className={classes.noEvidence}>
                No evidence uploaded. Payoff letters are uploaded from the Payoff task.
              </Typography>
            ) : uploading ? (
              <Box className={classes.uploadingRow}>
                <CircularProgress size={20} />
                <Typography>Uploading…</Typography>
              </Box>
            ) : (
              <FileUploadArea
                items={[]}
                setItems={(updater: FileUpload[] | ((prev: FileUpload[]) => FileUpload[])) => {
                  const resolved = typeof updater === 'function' ? updater([]) : updater;
                  if (resolved[0]?.file) {
                    handleUpload(resolved[0].file);
                  }
                }}
              />
            )}
            {internalView && payoffRecord?.filePath && (
              <Box className={classes.extractedData}>
                <Box className={classes.extractedRow}>
                  <Typography className={classes.extractedLabel}>
                    Property Address
                    {addressMismatch && <Error className={classes.warningIcon} />}
                  </Typography>
                  <Typography className={classes.extractedValue}>
                    {payoffRecord.propertyAddress}
                  </Typography>
                </Box>
                <Box className={classes.extractedTwoCol}>
                  <Box className={classes.extractedCol}>
                    <Typography className={classes.extractedLabel}>
                      Borrower
                    </Typography>
                    <Typography className={classes.extractedValue}>
                      {payoffRecord.borrowerName}
                    </Typography>
                  </Box>
                  <Box className={classes.extractedCol}>
                    <Typography className={classes.extractedLabel}>
                      Lender
                    </Typography>
                    <Typography className={classes.extractedValue}>
                      {payoffRecord.lenderName}
                    </Typography>
                  </Box>
                </Box>
                <Box className={classes.extractedTwoCol}>
                  <Box className={classes.extractedCol}>
                    <Typography className={classes.extractedLabel}>
                      Maturity Date
                    </Typography>
                    <Typography className={classes.extractedValue}>
                      {payoffRecord.maturityDate}
                    </Typography>
                  </Box>
                  <Box className={classes.extractedCol}>
                    <Typography className={classes.extractedLabel}>
                      Payoff Date
                    </Typography>
                    <Typography className={classes.extractedValue}>
                      {payoffRecord.payoffDate}
                    </Typography>
                  </Box>
                </Box>
                <PayoffAmountBreakdown payoffRecord={payoffRecord} />
              </Box>
            )}
          </Grid>
        </Grid>
        </AccordionDetails>
      </Accordion>
      {editLienModalOpen && (
        <AddEditLienModal
          open={editLienModalOpen}
          onClose={() => setEditLienModalOpen(false)}
          editingLienId={lien.propertyLienId}
        />
      )}
      <ConfirmDialog
        open={deleteConfirmOpen}
        title={`Delete Payoff ${ordinal}?`}
        body="The payoff record and all associated payoff letters will be permanently removed. This action can’t be undone."
        confirmButtonText="Delete"
        handleClose={() => setDeleteConfirmOpen(false)}
        handleCancel={() => setDeleteConfirmOpen(false)}
        handleConfirm={handleDelete}
      />
      </>
    );
  }
);
